import { forwardRef } from "react";
import {
  Button, HStack, Icon, Text, Tooltip, VStack,
} from "@chakra-ui/react";
import ProfileAvatar from "components/ProfileAvatar";
import { FiCheck } from "react-icons/fi";
import { format, formatDistanceToNow } from "date-fns";
import { RelationType } from "models/v2/networkRelation";
import { ReminderRow } from "../types";

const ROLE_LABEL: Record<RelationType, string> = {
  new_hire: "New hire",
  manager: "Manager",
  buddy: "Onboarding buddy",
  mentor: "Mentor",
  people_team_contact: "People team contact",
  recruiter: "Recruiter",
  it_team_contact: "IT",
};

function primaryRoleLabel(roles: RelationType[]): string | undefined {
  const ordered: RelationType[] = [
    "manager", "it_team_contact", "buddy", "mentor", "people_team_contact", "recruiter", "new_hire",
  ];
  const found = ordered.find((r) => roles.includes(r));
  return found ? ROLE_LABEL[found] : undefined;
}

function pluralize(count: number, singular: string, plural?: string): string {
  return count === 1 ? singular : (plural ?? `${singular}s`);
}

interface RelativeTimeProps {
  iso: string;
  color: string;
  prefix?: string;
}

function RelativeTime({ iso, color, prefix }: RelativeTimeProps) {
  const date = new Date(iso);
  return (
    <Tooltip label={format(date, "MMM d, yyyy 'at' h:mm a")} placement="top" hasArrow openDelay={300}>
      <Text as="span" color={color} cursor="default">
        {prefix}
        {formatDistanceToNow(date, { addSuffix: true })}
      </Text>
    </Tooltip>
  );
}

interface SentenceProps {
  row: ReminderRow;
}

function StakeholderSentence({ row }: SentenceProps) {
  const openCount = row.overdueCount + row.urgentCount + row.activeCount;
  const role = primaryRoleLabel(row.roles);
  return (
    <Text fontSize="sm" color="fg.default" lineHeight="short">
      <Text as="span" fontWeight="semibold">{row.user.fullName}</Text>
      {role && (
        <Text as="span" color="fg.muted">
          {" "}
          ({role})
        </Text>
      )}
      {" "}
      has
      {" "}
      {openCount}
      {" "}
      open
      {" "}
      {pluralize(openCount, "task")}
      {row.overdueCount > 0 && (
        <>
          {" "}
          (
          <Text as="span" color="red.600" fontWeight="semibold">
            {row.overdueCount}
            {" "}
            overdue
          </Text>
          )
        </>
      )}
    </Text>
  );
}

function YouSentence({ row }: SentenceProps) {
  const openCount = row.overdueCount + row.urgentCount + row.activeCount;
  return (
    <Text fontSize="sm" color="fg.default" lineHeight="short">
      <Text as="span" fontWeight="semibold">You</Text>
      {" "}
      have
      {" "}
      {openCount}
      {" "}
      open
      {" "}
      {pluralize(openCount, "task")}
      {row.overdueCount > 0 && (
        <>
          {" "}
          (
          <Text as="span" color="red.600" fontWeight="semibold">
            {row.overdueCount}
            {" "}
            overdue
          </Text>
          )
        </>
      )}
    </Text>
  );
}

interface StatusLineProps {
  row: ReminderRow;
}

function StatusLine({ row }: StatusLineProps) {
  if (row.isSelf) return null;

  if (row.recency === "never") {
    return (
      <Text fontSize="xs" color="orange.500" mt={1} fontWeight="medium">
        Not reminded yet
      </Text>
    );
  }

  if (row.recency === "stale" && row.lastReminderAt) {
    return (
      <Text fontSize="xs" mt={1}>
        <RelativeTime iso={row.lastReminderAt} color="fg.muted" prefix="Last reminded " />
      </Text>
    );
  }

  if (row.recency === "recent" && row.lastReminderAt) {
    return (
      <HStack spacing={1} mt={1} fontSize="xs">
        <Icon as={FiCheck} color="green.600" boxSize={3.5} aria-hidden />
        <Text as="span" color="green.700" fontWeight="medium">
          <RelativeTime iso={row.lastReminderAt} color="green.700" prefix="Reminded " />
        </Text>
      </HStack>
    );
  }

  return null;
}

interface ReminderRowItemProps {
  row: ReminderRow;
  onAction: (row: ReminderRow) => void;
}

const ReminderRowItem = forwardRef<HTMLButtonElement, ReminderRowItemProps>(({ row, onAction }, ref) => {
  const isRecent = row.recency === "recent";
  const isSelf = row.isSelf;

  let buttonLabel: string;
  let buttonVariant: "solid" | "outline";
  let buttonColorScheme: "cdsIndigo" | "gray";

  if (isSelf) {
    buttonLabel = "Go to my tasks";
    buttonVariant = "outline";
    buttonColorScheme = "gray";
  } else if (isRecent) {
    buttonLabel = "Remind again";
    buttonVariant = "outline";
    buttonColorScheme = "gray";
  } else {
    buttonLabel = "Send reminder";
    buttonVariant = "solid";
    buttonColorScheme = "cdsIndigo";
  }

  return (
    <HStack
      align={isSelf ? "center" : "flex-start"}
      justify="space-between"
      spacing={4}
      py={4}
      px={1}
      w="full"
    >
      <HStack align={isSelf ? "center" : "flex-start"} spacing={3} flex={1} minW={0}>
        <ProfileAvatar user={row.user} size="sm" />
        <VStack align="start" spacing={0} flex={1} minW={0}>
          {isSelf ? <YouSentence row={row} /> : <StakeholderSentence row={row} />}
          <StatusLine row={row} />
        </VStack>
      </HStack>
      <Button
        ref={ref}
        size="sm"
        h={7}
        variant={buttonVariant}
        colorScheme={buttonColorScheme}
        onClick={() => onAction(row)}
        flexShrink={0}
      >
        {buttonLabel}
      </Button>
    </HStack>
  );
});
ReminderRowItem.displayName = "ReminderRowItem";

export default ReminderRowItem;
